import { useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { FiltroDeTags } from '@/components/tags/FiltroDeTags';
import { Button } from '@/components/ui/button';
import { DestinoDosPopovers } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

export interface BarraDaListaProps {
  /** As abas (`AbasDaLista`): à esquerda da busca; no celular, um seletor ao lado de "Filtros". */
  abas?: ReactNode;
  /** A busca (`CampoDeBusca` ou `BuscaInteligente`), que ocupa o resto da linha. */
  busca?: ReactNode;
  /** Os filtros (`FiltroDaLista` e afins), na linha de baixo. */
  filtros?: ReactNode;
  /** O filtro por tags: a barra o põe sempre depois dos `filtros`, em toda tela. */
  filtroDeTags?: { value: string[]; onChange: (ids: string[]) => void };
  /** A ordem da lista (um `FiltroDaLista`), colada à direita das tags. */
  ordem?: ReactNode;
  /**
   * Quantos dos `filtros` estão ligados (sem contar tags e ordem): o número do botão
   * "Filtros" no celular, onde os filtros ficam guardados numa folha.
   */
  filtrosAtivos?: number;
  /**
   * O que age sobre a seleção da lista (ex.: aplicar tags em lote), logo depois das
   * tags. Aparece na linha que já existe, sem empurrar a lista para baixo.
   */
  selecao?: ReactNode;
  /** O que fica à direita da linha dos filtros (espaço usado, lista/grade). */
  acoes?: ReactNode;
  className?: string;
}

/**
 * O topo das telas de lista (docs/PLANO_BARRA_DE_LISTA.md, no desenho do Rubens):
 * as abas à esquerda e a busca no resto da linha; embaixo, os filtros, as tags, a ordem
 * logo depois delas e as ações à direita.
 *
 * No celular (docs/PLANO_RESPONSIVIDADE_MOBILE.md §3.3) são duas linhas: a busca, e embaixo
 * o seletor da aba, o botão "Filtros" (filtros, tags e ordem numa folha que sobe de baixo),
 * a seleção e as ações. Antes eram até cinco faixas antes do primeiro registro.
 */
export function BarraDaLista({
  abas,
  busca,
  filtros,
  filtroDeTags,
  ordem,
  filtrosAtivos = 0,
  selecao,
  acoes,
  className,
}: BarraDaListaProps) {
  const largo = useMediaQuery('(min-width: 640px)');
  const [folhaAberta, setFolhaAberta] = useState(false);
  // O conteúdo da folha é o destino dos popovers dos filtros: fora dele, não rolavam.
  const [folha, setFolha] = useState<HTMLDivElement | null>(null);

  if (!largo) {
    const temFolha = !!(filtros || filtroDeTags || ordem);
    const ligados = filtrosAtivos + (filtroDeTags?.value.length ? 1 : 0);
    return (
      <div className={cn('space-y-3', className)}>
        {busca && <div className="min-w-0">{busca}</div>}
        {(abas || temFolha || selecao || acoes) && (
          <div className="flex flex-wrap items-center gap-2">
            {abas && <div className="flex min-w-0 max-w-full">{abas}</div>}
            {temFolha && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setFolhaAberta(true)}
                aria-label={ligados ? `Filtros: ${ligados} ligado${ligados > 1 ? 's' : ''}` : 'Filtros'}
                className={cn(
                  'h-11 shrink-0 gap-2 px-3 font-normal',
                  ligados > 0 && 'border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary',
                )}
              >
                <SlidersHorizontal className="h-4 w-4" aria-hidden />
                Filtros
                {ligados > 0 && <span className="tabular-nums">· {ligados}</span>}
              </Button>
            )}
            {selecao}
            {acoes && <div className="ml-auto flex items-center gap-2">{acoes}</div>}
          </div>
        )}
        {temFolha && (
          <Sheet open={folhaAberta} onOpenChange={setFolhaAberta}>
            <SheetContent ref={setFolha} side="bottom" className="max-h-[85dvh] overflow-y-auto rounded-t-2xl px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <SheetTitle className="px-1 pt-1 text-base">Filtros</SheetTitle>
              <SheetDescription className="sr-only">Filtros e ordem da lista</SheetDescription>
              {/* Cada gatilho na largura toda: na folha, eles são a lista de eixos. */}
              <DestinoDosPopovers.Provider value={folha}>
                <div className="mt-4 flex flex-col gap-2 [&_button[role=combobox]]:w-full">
                  {filtros}
                  {filtroDeTags && <FiltroDeTags value={filtroDeTags.value} onChange={filtroDeTags.onChange} />}
                  {ordem}
                </div>
              </DestinoDosPopovers.Provider>
              <Button type="button" className="mt-5 h-11 w-full" onClick={() => setFolhaAberta(false)}>
                Ver a lista
              </Button>
            </SheetContent>
          </Sheet>
        )}
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {(abas || busca) && (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:gap-4">
          {abas && <div className="min-w-0 max-w-full shrink-0">{abas}</div>}
          {busca && <div className="min-w-0 flex-1">{busca}</div>}
        </div>
      )}
      {(filtros || filtroDeTags || ordem || selecao || acoes) && (
        <div className="flex flex-wrap items-center gap-2">
          {filtros}
          {filtroDeTags && <FiltroDeTags value={filtroDeTags.value} onChange={filtroDeTags.onChange} />}
          {ordem}
          {selecao}
          {acoes && <div className="ml-auto flex items-center gap-3">{acoes}</div>}
        </div>
      )}
    </div>
  );
}
